import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Save, TrendingDown, Gauge } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const DED_LABELS = {
  screen_replaced: "Ekran dəyişilib",
  battery_replaced: "Batareya dəyişilib",
  back_replaced: "Arxa qapaq dəyişilib",
  repaired_dropped: "Təmirdə olub / yıxılıb",
  faceid_broken: "Face ID işləmir",
  scuffs: "Korpus cızıq / sürtülmə",
  chips: "Korpus əzik / sıyrıq",
  screen_scratches: "Ekranda cızıq",
  no_box: "Qutu yoxdur",
  no_cable: "Kabel yoxdur",
  esim_only: "Yalnız eSIM",
};
const COND_LABELS = { like_new: "Əla", good: "Yaxşı", fair: "Normal", poor: "Zədəli" };

const toPct = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, Math.round(v * 1000) / 10]));

const Field = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-2.5">
    <span className="text-sm text-neutral-300">{label}</span>
    <div className="flex items-center gap-1.5">
      <Input value={value} onChange={(e) => onChange(e.target.value)} type="number" data-testid={testid}
        className="bg-white/5 border-white/10 w-20 h-9 text-sm text-right" />
      <span className="text-neutral-500 text-sm">%</span>
    </div>
  </div>
);

export const PricingManager = () => {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing").then((r) => {
      setPricing({ deductions: toPct(r.data.deductions), condition: toPct(r.data.condition) });
    }).catch(() => {});
  }, []);

  if (!pricing) return <div className="text-neutral-500 text-sm">Yüklənir...</div>;

  const setDed = (k, v) => setPricing((p) => ({ ...p, deductions: { ...p.deductions, [k]: v } }));
  const setCond = (k, v) => setPricing((p) => ({ ...p, condition: { ...p.condition, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const deductions = Object.fromEntries(Object.entries(pricing.deductions).map(([k, v]) => [k, (parseFloat(v) || 0) / 100]));
      const condition = Object.fromEntries(Object.entries(pricing.condition).map(([k, v]) => [k, (parseFloat(v) || 0) / 100]));
      await api.put("/admin/pricing", { deductions, condition });
      toast.success("Qiymət qaydaları yadda saxlanıldı");
    } catch { toast.error("Xəta baş verdi"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="pricing-manager">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1 text-sm font-medium"><TrendingDown className="w-4 h-4" /> Endirimlər (çıxılan faiz)</div>
        <p className="text-xs text-neutral-500 mb-4">Hər qüsur üçün qiymətdən çıxılan faizi tənzimləyin.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.keys(DED_LABELS).map((k) => (
            <Field key={k} label={DED_LABELS[k]} value={pricing.deductions[k] ?? 0} onChange={(v) => setDed(k, v)} testid={`ded-${k}`} />
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1 text-sm font-medium"><Gauge className="w-4 h-4" /> Vəziyyət əmsalı (baza qiymətin %-i)</div>
        <p className="text-xs text-neutral-500 mb-4">Hər vəziyyət üçün baza qiymətin neçə faizinin ödəniləcəyi.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.keys(COND_LABELS).map((k) => (
            <Field key={k} label={COND_LABELS[k]} value={pricing.condition[k] ?? 0} onChange={(v) => setCond(k, v)} testid={`cond-${k}`} />
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="pricing-save-btn"
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-neutral-100 text-black font-medium disabled:opacity-50 hover:bg-white transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Yadda saxla
      </button>
    </div>
  );
};
